import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  InputNumber,
  Row,
  Col,
  Statistic,
  Typography,
  Table,
  Tag,
  DatePicker,
  Space,
  Button,
  Segmented,
  Alert,
  Tooltip,
  Checkbox,
} from "antd";
import {
  EuroCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import AdminPageLayout from "./layout/AdminPageLayout";
import useBookingStore from "../../stores/bookingStore";
import dayjs from "dayjs";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Tirano — regole imposta di soggiorno (Airbnb / locazione turistica):
const RATE_PER_PERSON_NIGHT = 2; // EUR a persona / notte
const MAX_BILLABLE_NIGHTS = 3; // notti consecutive massime soggette
const EXEMPT_UNDER_AGE = 12; // minori di 12 anni esenti

const euro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(
    n || 0,
  );
const nightsBetween = (ci, co) =>
  Math.max(0, dayjs(co).startOf("day").diff(dayjs(ci).startOf("day"), "day"));

const ImpostaSoggiorno = () => {
  const { bookings, loading, fetchBookings } = useBookingStore();

  const [range, setRange] = useState(null); // [dayjs, dayjs] | null = tutto
  const [defaultGuests, setDefaultGuests] = useState(2); // ospiti 12+ predefiniti
  const [overrides, setOverrides] = useState({}); // { [bookingId]: guests }
  const [excludeCancelled, setExcludeCancelled] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Quick time windows
  const setWindow = (key) => {
    const now = dayjs();
    if (key === "all") setRange(null);
    else if (key === "month") setRange([now.startOf("month"), now.endOf("month")]);
    else if (key === "year") setRange([now.startOf("year"), now.endOf("year")]);
    else if (key === "lastYear")
      setRange([
        now.subtract(1, "year").startOf("year"),
        now.subtract(1, "year").endOf("year"),
      ]);
  };

  const rows = useMemo(() => {
    const inRange = (ci) => {
      if (!range || !range[0] || !range[1]) return true;
      const d = dayjs(ci);
      return (
        !d.isBefore(range[0].startOf("day")) && !d.isAfter(range[1].endOf("day"))
      );
    };
    return (bookings || [])
      .filter((b) => (excludeCancelled ? b.status !== "cancelled" : true))
      .filter((b) => inRange(b.check_in))
      .map((b) => {
        const nights = nightsBetween(b.check_in, b.check_out);
        const billable = Math.min(nights, MAX_BILLABLE_NIGHTS);
        const guests = overrides[b.id] ?? defaultGuests;
        const tax = guests * RATE_PER_PERSON_NIGHT * billable;
        return {
          ...b,
          nights,
          billable,
          guests,
          tax,
          capped: nights > MAX_BILLABLE_NIGHTS,
        };
      })
      .sort((a, b) => dayjs(a.check_in).unix() - dayjs(b.check_in).unix());
  }, [bookings, range, defaultGuests, overrides, excludeCancelled]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          stays: acc.stays + 1,
          nights: acc.nights + r.nights,
          billable: acc.billable + r.billable,
          guestNights: acc.guestNights + r.guests * r.billable,
          tax: acc.tax + r.tax,
        }),
        { stays: 0, nights: 0, billable: 0, guestNights: 0, tax: 0 },
      ),
    [rows],
  );

  const columns = [
    {
      title: "Ospite",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Piattaforma",
      dataIndex: ["platform_details", "name"],
      key: "platform",
      render: (v) => (v ? <Tag>{v}</Tag> : "—"),
    },
    {
      title: "Check-in",
      dataIndex: "check_in",
      key: "check_in",
      render: (v) => dayjs(v).format("DD/MM/YYYY"),
    },
    {
      title: "Check-out",
      dataIndex: "check_out",
      key: "check_out",
      render: (v) => dayjs(v).format("DD/MM/YYYY"),
    },
    {
      title: "Notti",
      key: "nights",
      align: "center",
      render: (_, r) =>
        r.capped ? (
          <Tooltip title={`${r.nights} notti — conteggiate solo ${MAX_BILLABLE_NIGHTS}`}>
            <span>
              {r.billable} <Text type="secondary">/ {r.nights}</Text>
            </span>
          </Tooltip>
        ) : (
          r.nights
        ),
    },
    {
      title: (
        <Tooltip title={`Ospiti soggetti (${EXEMPT_UNDER_AGE}+ anni). Modificabile per prenotazione.`}>
          Ospiti (12+)
        </Tooltip>
      ),
      key: "guests",
      align: "center",
      render: (_, r) => (
        <InputNumber
          min={0}
          size="small"
          style={{ width: 64 }}
          value={r.guests}
          onChange={(val) =>
            setOverrides((o) => ({ ...o, [r.id]: val ?? 0 }))
          }
        />
      ),
    },
    {
      title: "Imposta",
      dataIndex: "tax",
      key: "tax",
      align: "right",
      sorter: (a, b) => a.tax - b.tax,
      render: (v) => <Text strong>{euro(v)}</Text>,
    },
  ];

  return (
    <AdminPageLayout
      icon={<EuroCircleOutlined />}
      title="Imposta di Soggiorno"
      subtitle="Calcolo automatico dal calendario prenotazioni — Comune di Tirano"
      listItems={[
        `${euro(RATE_PER_PERSON_NIGHT)} a persona, a notte`,
        `Massimo ${MAX_BILLABLE_NIGHTS} notti consecutive`,
        `Esenti i minori di ${EXEMPT_UNDER_AGE} anni`,
      ]}
    >
      {/* Filters */}
      <Card size="small" style={{ marginBottom: 24 }}>
        <Space wrap size="large" align="end">
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary">Finestra temporale</Text>
            </div>
            <Space wrap>
              <Segmented
                options={[
                  { label: "Tutto", value: "all" },
                  { label: "Mese", value: "month" },
                  { label: "Anno", value: "year" },
                  { label: "Anno scorso", value: "lastYear" },
                ]}
                onChange={setWindow}
                defaultValue="all"
              />
              <RangePicker
                format="DD/MM/YYYY"
                value={range}
                onChange={setRange}
                allowClear
              />
            </Space>
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary">Ospiti predefiniti (12+)</Text>
            </div>
            <InputNumber
              min={0}
              value={defaultGuests}
              onChange={(v) => setDefaultGuests(v ?? 0)}
            />
          </div>
          <Checkbox
            checked={excludeCancelled}
            onChange={(e) => setExcludeCancelled(e.target.checked)}
          >
            Escludi cancellate
          </Checkbox>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setOverrides({});
              fetchBookings();
            }}
          >
            Aggiorna
          </Button>
        </Space>
      </Card>

      {/* Totals */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Imposta totale"
              value={totals.tax}
              precision={2}
              prefix="€"
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Soggiorni" value={totals.stays} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Notti tassabili"
              value={totals.billable}
              suffix={`/ ${totals.nights}`}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Presenze tassabili" value={totals.guestNights} />
          </Card>
        </Col>
      </Row>

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="Il numero di ospiti non è disponibile nelle prenotazioni importate: viene usato il valore predefinito qui sopra, modificabile per singola prenotazione nella colonna «Ospiti (12+)». I minori di 12 anni sono esenti e vanno esclusi dal conteggio."
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <Text strong>Totale ({totals.stays} soggiorni)</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="center">
                <Text strong>{totals.billable}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="center">
                <Text strong>{totals.guestNights}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <Text strong style={{ color: "#3f8600" }}>{euro(totals.tax)}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </AdminPageLayout>
  );
};

export default ImpostaSoggiorno;
